import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  archiveMySupport,
  getMySupports,
  updateSupport,
} from "../../../services/SupportService";
import {
  SUPPORT_CONTACT_METHODS,
  SUPPORT_ISSUE_STARTED_OPTIONS,
  SUPPORT_PRIORITIES,
  SUPPORT_TYPES,
  getSupportPriorityDisplay,
  getSupportSubcategories,
  getSupportSubcategoryLabel,
  getSupportTypeMeta,
  typeHasSubcategories,
} from "../../../contants/supportTypes";
import { toast } from "react-toastify";
import socket from "../../../services/socket";
import "react-datepicker/dist/react-datepicker.css";
import "./mySupportRequests.css";

const PAGE_SIZE = 7;

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const EMPTY_EDIT_FORM = {
  type: "",
  subcategory: "",
  customSubcategory: "",
  customType: "",
  priority: "moyenne",
  issueStarted: "today",
  preferredContactMethod: "email",
  description: "",
  recommendation: "",
};

const TicketActionsToggle = forwardRef(function TicketActionsToggle(
  { onClick, disabled, ...props },
  ref
) {
  return (
    <button
      type="button"
      {...props}
      ref={ref}
      className="msr-ticket__menu-btn"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      <i className="fas fa-ellipsis-v" aria-hidden="true" />
    </button>
  );
});

const STATUS_LABELS = {
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
};

const PRIORITY_LABELS = {
  faible: "Low",
  moyenne: "Medium",
  haute: "High",
  critique: "Critical",
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "priority", label: "Priority" },
];

const PRIORITY_RANK = { critique: 0, haute: 1, moyenne: 2, faible: 3 };

const formatTicketRef = (id) => `#${String(id).slice(-6).toUpperCase()}`;

const formatCardDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatMessageTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getPersonName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim() || "Unknown";

const getInitials = (person) => {
  const first = person?.firstName?.[0] || "";
  const last = person?.lastName?.[0] || "";
  return `${first}${last}`.toUpperCase() || "?";
};

const getRoleLabel = (role) => {
  if (role === "superAdmin") return "Support";
  if (role === "responsable" || role === "resFranchise") return "Requester";
  return "Requester";
};

const getVisibleReplies = (support) =>
  (support?.replies || []).filter((reply) => reply.kind !== "internal_note");

const getLatestMessage = (support) => {
  const replies = getVisibleReplies(support);
  if (replies.length > 0) {
    const last = replies[replies.length - 1];
    return {
      message: last.message || "",
      author: last.author,
      authorRole: last.authorRole || last.author?.role,
      createdAt: last.createdAt,
    };
  }
  return {
    message: support.description || "",
    author: support.user,
    authorRole: support.user?.role || "responsable",
    createdAt: support.createdAt,
  };
};

const isAwaitingReply = (support) => {
  if (support.status === "resolved") return false;
  const replies = getVisibleReplies(support);
  const supportReplied = replies.some((reply) => {
    const role = reply.authorRole || reply.author?.role;
    return role === "superAdmin";
  });
  // Show when ClickToEat support has not replied to this request yet
  return !supportReplied;
};

const getTicketSubject = (support) => {
  const subLabel = getSupportSubcategoryLabel(
    support.type,
    support.subcategory,
    support.customSubcategory
  );
  if (subLabel) return subLabel;
  if (support.type === "other" && support.customType?.trim()) {
    return support.customType.trim();
  }
  return getSupportTypeMeta(support.type)?.label || "Support request";
};

const getTicketCategory = (support) => {
  if (support.type === "other" && support.customType?.trim()) return "Other";
  return getSupportTypeMeta(support.type)?.label || support.type || "—";
};

const getTypeTone = (type) => {
  const tones = {
    bug: "rose",
    payment_subscription: "peach",
    account: "violet",
    feature_request: "mint",
    question_assistance: "sky",
    security: "rose",
    performance: "amber",
    reports: "sky",
    notifications: "mint",
  };
  return tones[type] || "peach";
};

const MySupportRequests = () => {
  const navigate = useNavigate();
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editSupport, setEditSupport] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [savingEdit, setSavingEdit] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  const editSubcategories = useMemo(
    () => getSupportSubcategories(editForm.type),
    [editForm.type]
  );
  const showEditSubcategory = Boolean(editForm.type) && typeHasSubcategories(editForm.type);

  const editPreviewSupport = useMemo(
    () => (editSupport ? { ...editSupport, ...editForm } : null),
    [editSupport, editForm]
  );

  const fetchSupports = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const response = await getMySupports();
      setSupports(response.supports || []);
    } catch (error) {
      toast.error(error.message || "Failed to load support requests");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSupports();
  }, [fetchSupports]);

  useEffect(() => {
    const onCreated = () => fetchSupports(true);
    window.addEventListener("support-created", onCreated);
    return () => window.removeEventListener("support-created", onCreated);
  }, [fetchSupports]);

  useEffect(() => {
    const onSupportReply = (payload) => {
      const supportId = payload?.supportId || payload?.support?._id;
      const incoming = payload?.support;
      if (incoming?._id) {
        setSupports((prev) =>
          prev.map((item) =>
            String(item._id) === String(incoming._id) ? { ...item, ...incoming } : item
          )
        );
        return;
      }
      if (supportId && payload?.reply) {
        setSupports((prev) =>
          prev.map((item) => {
            if (String(item._id) !== String(supportId)) return item;
            const replies = Array.isArray(item.replies) ? [...item.replies] : [];
            const replyId = payload.reply._id && String(payload.reply._id);
            if (replyId && replies.some((reply) => String(reply._id) === replyId)) return item;
            replies.push(payload.reply);
            return { ...item, replies, status: payload.status || item.status };
          })
        );
        return;
      }
      fetchSupports(true);
    };
    const onNewSupportReply = (notif) => {
      const supportId = notif?.extraData?.supportId || notif?.supportId;
      if (!supportId) return;
      fetchSupports(true);
    };

    const onConnect = () => fetchSupports(true);

    socket.on("support-reply", onSupportReply);
    socket.on("new-support-reply", onNewSupportReply);
    socket.on("connect", onConnect);

    const pollId = window.setInterval(() => {
      if (document.visibilityState !== "hidden") fetchSupports(true);
    }, 4000);

    return () => {
      window.clearInterval(pollId);
      socket.off("support-reply", onSupportReply);
      socket.off("new-support-reply", onNewSupportReply);
      socket.off("connect", onConnect);
    };
  }, [fetchSupports]);

  const openCount = useMemo(
    () => supports.filter((s) => s.status !== "resolved").length,
    [supports]
  );

  const awaitingCount = useMemo(
    () => supports.filter((s) => isAwaitingReply(s)).length,
    [supports]
  );

  const filteredSupports = useMemo(() => {
    const query = search.trim().toLowerCase();
    const fromBound = dateFrom ? startOfDay(dateFrom).getTime() : null;
    const toBound = dateTo ? endOfDay(dateTo).getTime() : null;

    let list = supports.filter((support) => {
      if (statusFilter !== "all" && (support.status || "pending") !== statusFilter) {
        return false;
      }
      if (priorityFilter !== "all" && support.priority !== priorityFilter) {
        return false;
      }

      if (fromBound || toBound) {
        const createdAt = new Date(support.createdAt).getTime();
        if (Number.isNaN(createdAt)) return false;
        if (fromBound && createdAt < fromBound) return false;
        if (toBound && createdAt > toBound) return false;
      }

      if (!query) return true;

      const subject = getTicketSubject(support).toLowerCase();
      const category = getTicketCategory(support).toLowerCase();
      const ref = formatTicketRef(support._id).toLowerCase();
      const description = (support.description || "").toLowerCase();
      const requester = getPersonName(support.user).toLowerCase();

      return (
        subject.includes(query) ||
        category.includes(query) ||
        ref.includes(query) ||
        description.includes(query) ||
        requester.includes(query) ||
        String(support._id).toLowerCase().includes(query)
      );
    });

    list = [...list].sort((a, b) => {
      if (sortBy === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      if (sortBy === "priority") {
        const rankDiff =
          (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        if (rankDiff !== 0) return rankDiff;
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return list;
  }, [supports, search, statusFilter, priorityFilter, sortBy, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filteredSupports.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, priorityFilter, sortBy, dateFrom, dateTo]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedSupports = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSupports.slice(start, start + PAGE_SIZE);
  }, [filteredSupports, currentPage]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    return Array.from({ length: 5 }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  const hasActiveFilters =
    search.trim() !== "" ||
    statusFilter !== "all" ||
    priorityFilter !== "all" ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setSortBy("newest");
    setDateFrom(null);
    setDateTo(null);
    setCurrentPage(1);
  };

  const openTicket = (supportId) => {
    navigate(`/my-support-requests/${supportId}`);
  };

  const openNewTicket = () => {
    window.dispatchEvent(new CustomEvent("open-support-modal"));
  };

  const openUpdateModal = (support) => {
    setEditSupport(support);
    setEditForm({
      type: support.type || "",
      subcategory: support.subcategory || "",
      customSubcategory: support.customSubcategory || "",
      customType: support.customType || "",
      priority: support.priority || "moyenne",
      issueStarted: support.issueStarted || "today",
      preferredContactMethod: support.preferredContactMethod || "email",
      description: support.description || "",
      recommendation: support.recommendation || "",
    });
  };

  const closeUpdateModal = () => {
    if (savingEdit) return;
    setEditSupport(null);
    setEditForm(EMPTY_EDIT_FORM);
  };

  const handleEditTypeChange = (nextType) => {
    setEditForm((prev) => ({
      ...prev,
      type: nextType,
      subcategory: "",
      customSubcategory: "",
      customType: nextType !== "other" ? "" : prev.customType,
    }));
  };

  const handleSaveUpdate = async (e) => {
    e.preventDefault();
    if (!editSupport) return;

    const description = editForm.description.trim();
    if (!editForm.type) {
      toast.error("Please select a type");
      return;
    }
    if (!description) {
      toast.error("Description is required");
      return;
    }
    if (editForm.type === "other" && !editForm.customType.trim()) {
      toast.error("Please provide a custom type");
      return;
    }
    if (showEditSubcategory && !editForm.subcategory) {
      toast.error("Please select a subcategory");
      return;
    }
    if (editForm.subcategory === "other" && !editForm.customSubcategory.trim()) {
      toast.error("Please specify the subcategory");
      return;
    }

    const payload = {
      type: editForm.type,
      customType: editForm.type === "other" ? editForm.customType.trim() : "",
      subcategory: showEditSubcategory ? editForm.subcategory : "",
      customSubcategory:
        editForm.subcategory === "other" ? editForm.customSubcategory.trim() : "",
      description,
      recommendation: editForm.recommendation.trim(),
      priority: editForm.priority,
      issueStarted: editForm.issueStarted,
      preferredContactMethod: editForm.preferredContactMethod,
    };

    setSavingEdit(true);
    try {
      const response = await updateSupport(editSupport._id, payload);
      const updated = response.data;
      setSupports((prev) =>
        prev.map((item) =>
          item._id === editSupport._id
            ? {
                ...item,
                ...payload,
                ...(updated || {}),
              }
            : item
        )
      );
      toast.success(response.message || "Support request updated");
      setEditSupport(null);
      setEditForm(EMPTY_EDIT_FORM);
    } catch (error) {
      toast.error(error.message || "Failed to update support request");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleArchive = async (support) => {
    const result = await Swal.fire({
      title: "Archive this request?",
      text: "It will be removed from your support tickets list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Archive",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#16a34a",
    });

    if (!result.isConfirmed) return;

    setArchivingId(support._id);
    try {
      const response = await archiveMySupport(support._id);
      setSupports((prev) => prev.filter((item) => item._id !== support._id));
      toast.success(response.message || "Support request archived");
    } catch (error) {
      toast.error(error.message || "Failed to archive support request");
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <>
      <PageTitle motherMenu="Support" activeMenu="My requests" />
      <div className="msr-page">
        <header className="msr-top">
          <div className="msr-top__text">
            <h1 className="msr-top__title">Support tickets</h1>
            <p className="msr-top__subtitle">
              {loading
                ? "Loading your threads…"
                : `${openCount} open thread${openCount === 1 ? "" : "s"} · ${awaitingCount} awaiting reply`}
            </p>
          </div>
          <div className="msr-top__actions">
            <button
              type="button"
              className="msr-icon-btn"
              onClick={() => fetchSupports(true)}
              disabled={loading || refreshing}
              aria-label="Refresh"
              title="Refresh"
            >
              <i className={`fas fa-sync-alt${refreshing ? " fa-spin" : ""}`} aria-hidden="true" />
            </button>
            <button type="button" className="msr-new-btn" onClick={openNewTicket}>
              New ticket
            </button>
          </div>
        </header>

        <div className="msr-toolbar">
          <label className="msr-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets, requesters, or IDs"
            />
          </label>

          <div className="msr-filters">
            <select
              className={`msr-filter${statusFilter !== "all" ? " msr-filter--active" : ""}`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
            </select>

            <select
              className={`msr-filter${priorityFilter !== "all" ? " msr-filter--active" : ""}`}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              aria-label="Filter by priority"
            >
              <option value="all">Priority</option>
              {SUPPORT_PRIORITIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {PRIORITY_LABELS[item.value] || item.label}
                </option>
              ))}
            </select>

            <div
              className={`msr-date-range${dateFrom || dateTo ? " msr-date-range--active" : ""}`}
            >
              <i className="fas fa-calendar-days" aria-hidden="true" />
              <DatePicker
                selected={dateFrom}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setDateFrom(start);
                  setDateTo(end);
                }}
                selectsRange
                startDate={dateFrom}
                endDate={dateTo}
                maxDate={new Date()}
                placeholderText="Date A - Date B"
                className="msr-date-range__input"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
            </div>

            <select
              className="msr-filter"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort tickets"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                type="button"
                className="msr-filter-reset"
                onClick={resetFilters}
              >
                <i className="fas fa-rotate-left" aria-hidden="true" />
                Reset
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="msr-loading">
            <div className="msr-spinner" aria-hidden="true" />
            <p>Loading your requests...</p>
          </div>
        ) : supports.length === 0 ? (
          <div className="msr-empty">
            <div className="msr-empty__icon" aria-hidden="true">
              <i className="fas fa-inbox" />
            </div>
            <h3>No support requests yet</h3>
            <p>Use New ticket to submit your first request.</p>
            <button type="button" className="msr-new-btn" onClick={openNewTicket}>
              New ticket
            </button>
          </div>
        ) : filteredSupports.length === 0 ? (
          <div className="msr-empty">
            <div className="msr-empty__icon" aria-hidden="true">
              <i className="fas fa-filter" />
            </div>
            <h3>No tickets match your filters</h3>
            <p>Try another search, date range, or reset the filters.</p>
            {hasActiveFilters && (
              <button type="button" className="msr-filter-reset" onClick={resetFilters}>
                <i className="fas fa-rotate-left" aria-hidden="true" />
                Reset filters
              </button>
            )}
          </div>
        ) : (
          <>
          <ul className="msr-list" role="list">
            {paginatedSupports.map((support) => {
              const status = support.status || "pending";
              const typeMeta = getSupportTypeMeta(support.type);
              const subject = getTicketSubject(support);
              const category = getTicketCategory(support);
              const ticketRef = formatTicketRef(support._id);
              const replyCount = getVisibleReplies(support).length;
              const latest = getLatestMessage(support);
              const awaiting = isAwaitingReply(support);
              const priority = support.priority || "moyenne";
              const canUpdate = status === "pending";
              const canArchive = status === "resolved";
              const showActions = canUpdate || canArchive;

              return (
                <li key={support._id}>
                  <div className="msr-ticket msr-ticket--accent">
                    <button
                      type="button"
                      className="msr-ticket__open"
                      onClick={() => openTicket(support._id)}
                      aria-label={`Open ticket ${ticketRef}: ${subject}`}
                    >
                      <div className="msr-ticket__main">
                        <div className="msr-ticket__head">
                          <span
                            className={`msr-ticket__icon msr-ticket__icon--${getTypeTone(support.type)}`}
                            aria-hidden="true"
                          >
                            {typeMeta?.emoji || "🎫"}
                          </span>

                          <div className="msr-ticket__title-wrap">
                            <span className="msr-ticket__subject">{subject}</span>
                            <span className="msr-ticket__category">· {category}</span>
                          </div>

                          <span className="msr-ticket__ref">{ticketRef}</span>
                        </div>

                        <div className="msr-ticket__preview">
                          <span
                            className={`msr-ticket__avatar${
                              latest.authorRole === "superAdmin"
                                ? " msr-ticket__avatar--support"
                                : ""
                            }`}
                            aria-hidden="true"
                          >
                            {getInitials(latest.author)}
                          </span>

                          <div className="msr-ticket__msg">
                            <div className="msr-ticket__msg-meta">
                              <strong>{getPersonName(latest.author)}</strong>
                              <span>{getRoleLabel(latest.authorRole)}</span>
                            </div>
                            <p className="msr-ticket__msg-text">
                              {latest.message || "—"}
                            </p>
                          </div>

                          <div className="msr-ticket__preview-side">
                            <span className="msr-ticket__msg-time">
                              {formatMessageTime(latest.createdAt)}
                            </span>
                            {awaiting && (
                              <span className="msr-ticket__awaiting">Awaiting reply</span>
                            )}
                          </div>
                        </div>

                        <div className="msr-ticket__foot">
                          <div className="msr-ticket__tags">
                            <span className={`msr-pill msr-pill--priority-${priority}`}>
                              <span aria-hidden="true" />
                              {PRIORITY_LABELS[priority] ||
                                getSupportPriorityDisplay(priority)}
                            </span>
                            <span className={`msr-pill msr-pill--status-${status}`}>
                              <span aria-hidden="true" />
                              {STATUS_LABELS[status] || status}
                            </span>
                            {replyCount > 0 && (
                              <span className="msr-ticket__replies">
                                {replyCount} {replyCount === 1 ? "reply" : "replies"}
                              </span>
                            )}
                          </div>

                          <div className="msr-ticket__aside">
                            <span className="msr-ticket__date">
                              {formatCardDate(support.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <div className="msr-ticket__side">
                      {showActions && (
                        <Dropdown
                          align="end"
                          className="msr-ticket__menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Dropdown.Toggle
                            as={TicketActionsToggle}
                            id={`msr-actions-${support._id}`}
                            aria-label={`Actions for ticket ${ticketRef}`}
                            title="Actions"
                            disabled={archivingId === support._id}
                          />
                          <Dropdown.Menu className="msr-ticket__menu-dropdown">
                            {canUpdate && (
                              <Dropdown.Item
                                onClick={() => openUpdateModal(support)}
                                className="msr-ticket__menu-item"
                              >
                                <i className="fas fa-pen" aria-hidden="true" />
                                Update
                              </Dropdown.Item>
                            )}
                            {canArchive && (
                              <Dropdown.Item
                                onClick={() => handleArchive(support)}
                                className="msr-ticket__menu-item msr-ticket__menu-item--danger"
                                disabled={archivingId === support._id}
                              >
                                <i className="fas fa-archive" aria-hidden="true" />
                                {archivingId === support._id ? "Archiving…" : "Archive"}
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                      <button
                        type="button"
                        className="msr-ticket__arrow"
                        onClick={() => openTicket(support._id)}
                        aria-label={`Open ticket ${ticketRef}: ${subject}`}
                        title="Open request"
                      >
                        <i className="fas fa-chevron-right" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="msr-pagination">
              <span className="msr-pagination__info">
                Page {currentPage} of {totalPages}
                <span>
                  · {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, filteredSupports.length)} of{" "}
                  {filteredSupports.length}
                </span>
              </span>

              <div className="msr-pagination__controls">
                <button
                  type="button"
                  className="msr-pagination__btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  « Previous
                </button>

                {pageNumbers[0] > 1 && (
                  <span className="msr-pagination__ellipsis">…</span>
                )}

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`msr-pagination__btn${
                      currentPage === page ? " is-active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <span className="msr-pagination__ellipsis">…</span>
                )}

                <button
                  type="button"
                  className="msr-pagination__btn"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                >
                  Next »
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      <Modal
        show={Boolean(editSupport)}
        onHide={closeUpdateModal}
        centered
        className="msr-edit-modal"
        backdropClassName="msr-edit-modal-backdrop"
        dialogClassName="msr-edit-modal__dialog"
        contentClassName="msr-edit-modal__content"
      >
        <div className="msr-edit-modal__header">
          <div className="msr-edit-modal__header-text">
            <span className="msr-edit-modal__header-icon" aria-hidden="true">
              <i className="fas fa-pen" />
            </span>
            <div className="msr-edit-modal__header-copy">
              <h2 className="msr-edit-modal__title">Update request</h2>
              <p className="msr-edit-modal__subtitle">
                Edit your pending ticket before support replies
              </p>
            </div>
          </div>
          <button
            type="button"
            className="msr-edit-modal__close"
            onClick={closeUpdateModal}
            disabled={savingEdit}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <Form onSubmit={handleSaveUpdate} className="msr-edit-modal__form">
          <div className="msr-edit-modal__body">
            {editPreviewSupport && (
              <div className="msr-edit-modal__ticket">
                <span
                  className={`msr-edit-modal__ticket-icon msr-ticket__icon--${getTypeTone(editPreviewSupport.type)}`}
                  aria-hidden="true"
                >
                  {getSupportTypeMeta(editPreviewSupport.type)?.emoji || "🎫"}
                </span>
                <div className="msr-edit-modal__ticket-meta">
                  <strong>{getTicketSubject(editPreviewSupport)}</strong>
                  <span>
                    {formatTicketRef(editSupport._id)} ·{" "}
                    {getTicketCategory(editPreviewSupport)}
                  </span>
                </div>
                <span className="msr-edit-modal__ticket-status">Pending</span>
              </div>
            )}

            <section className="msr-edit-modal__section">
              <h3 className="msr-edit-modal__section-title">
                <i className="fas fa-list" aria-hidden="true" />
                Request type
              </h3>

              <div className="msr-edit-modal__field">
                <label className="msr-edit-modal__label" htmlFor="msr-edit-type">
                  Type
                  <span className="msr-edit-modal__required">Required</span>
                </label>
                <select
                  id="msr-edit-type"
                  className="msr-edit-modal__select"
                  value={editForm.type}
                  onChange={(e) => handleEditTypeChange(e.target.value)}
                  disabled={savingEdit}
                  required
                >
                  <option value="" disabled>
                    Select a type
                  </option>
                  {SUPPORT_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.emoji} {item.label}
                    </option>
                  ))}
                </select>
              </div>

              {showEditSubcategory ? (
                <div className="msr-edit-modal__field">
                  <label className="msr-edit-modal__label" htmlFor="msr-edit-subcategory">
                    Subcategory
                    <span className="msr-edit-modal__required">Required</span>
                  </label>
                  <select
                    id="msr-edit-subcategory"
                    className="msr-edit-modal__select"
                    value={editForm.subcategory}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        subcategory: e.target.value,
                        customSubcategory:
                          e.target.value !== "other" ? "" : prev.customSubcategory,
                      }))
                    }
                    disabled={savingEdit}
                    required
                  >
                    <option value="" disabled>
                      Select a subcategory
                    </option>
                    {editSubcategories.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {showEditSubcategory && editForm.subcategory === "other" ? (
                <div className="msr-edit-modal__field">
                  <label
                    className="msr-edit-modal__label"
                    htmlFor="msr-edit-custom-subcategory"
                  >
                    Custom subcategory
                    <span className="msr-edit-modal__required">Required</span>
                  </label>
                  <input
                    id="msr-edit-custom-subcategory"
                    className="msr-edit-modal__input"
                    type="text"
                    value={editForm.customSubcategory}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        customSubcategory: e.target.value,
                      }))
                    }
                    disabled={savingEdit}
                    placeholder="Please specify the subcategory…"
                    required
                  />
                </div>
              ) : null}

              {editForm.type === "other" ? (
                <div className="msr-edit-modal__field">
                  <label className="msr-edit-modal__label" htmlFor="msr-edit-custom-type">
                    Custom type
                    <span className="msr-edit-modal__required">Required</span>
                  </label>
                  <input
                    id="msr-edit-custom-type"
                    className="msr-edit-modal__input"
                    type="text"
                    value={editForm.customType}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, customType: e.target.value }))
                    }
                    disabled={savingEdit}
                    placeholder="Please specify the type…"
                    required
                  />
                </div>
              ) : null}
            </section>

            <section className="msr-edit-modal__section">
              <h3 className="msr-edit-modal__section-title">
                <i className="fas fa-sliders-h" aria-hidden="true" />
                Priority &amp; contact
              </h3>

              <div className="msr-edit-modal__field">
                <span className="msr-edit-modal__label">
                  Priority
                  <span className="msr-edit-modal__required">Required</span>
                </span>
                <div className="msr-edit-modal__chips" role="group" aria-label="Priority">
                  {SUPPORT_PRIORITIES.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={`msr-edit-modal__chip${
                        editForm.priority === item.value ? " is-active" : ""
                      }`}
                      onClick={() =>
                        setEditForm((prev) => ({ ...prev, priority: item.value }))
                      }
                      disabled={savingEdit}
                    >
                      {item.emoji} {PRIORITY_LABELS[item.value] || item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="msr-edit-modal__grid">
                <div className="msr-edit-modal__field">
                  <label className="msr-edit-modal__label" htmlFor="msr-edit-issue-started">
                    When did the issue start?
                  </label>
                  <select
                    id="msr-edit-issue-started"
                    className="msr-edit-modal__select"
                    value={editForm.issueStarted}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        issueStarted: e.target.value,
                      }))
                    }
                    disabled={savingEdit}
                    required
                  >
                    {SUPPORT_ISSUE_STARTED_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="msr-edit-modal__field">
                  <label className="msr-edit-modal__label" htmlFor="msr-edit-contact">
                    Preferred contact method
                  </label>
                  <select
                    id="msr-edit-contact"
                    className="msr-edit-modal__select"
                    value={editForm.preferredContactMethod}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        preferredContactMethod: e.target.value,
                      }))
                    }
                    disabled={savingEdit}
                    required
                  >
                    {SUPPORT_CONTACT_METHODS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.emoji} {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="msr-edit-modal__section">
              <h3 className="msr-edit-modal__section-title">
                <i className="fas fa-align-left" aria-hidden="true" />
                Details
              </h3>

              <div className="msr-edit-modal__field">
                <label className="msr-edit-modal__label" htmlFor="msr-edit-description">
                  Description
                  <span className="msr-edit-modal__required">Required</span>
                </label>
                <textarea
                  id="msr-edit-description"
                  className="msr-edit-modal__textarea"
                  rows={5}
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  disabled={savingEdit}
                  required
                  placeholder="Describe the issue clearly so our team can help faster…"
                />
                <div className="msr-edit-modal__hint">
                  <span>Be specific about what you see and what you expected.</span>
                  <span>{editForm.description.trim().length} chars</span>
                </div>
              </div>

              <div className="msr-edit-modal__field">
                <label className="msr-edit-modal__label" htmlFor="msr-edit-recommendation">
                  Recommendation
                  <span className="msr-edit-modal__optional">Optional</span>
                </label>
                <textarea
                  id="msr-edit-recommendation"
                  className="msr-edit-modal__textarea msr-edit-modal__textarea--sm"
                  rows={3}
                  value={editForm.recommendation}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      recommendation: e.target.value,
                    }))
                  }
                  disabled={savingEdit}
                  placeholder="Any suggestion or preferred outcome…"
                />
              </div>
            </section>
          </div>

          <div className="msr-edit-modal__footer">
            <button
              type="button"
              className="msr-edit-modal__cancel"
              onClick={closeUpdateModal}
              disabled={savingEdit}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="msr-edit-modal__save"
              disabled={savingEdit || !editForm.description.trim() || !editForm.type}
            >
              {savingEdit ? (
                <>
                  <span className="msr-edit-modal__spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-check" aria-hidden="true" />
                  Save changes
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default MySupportRequests;
